import React, { useCallback, useEffect, useState } from 'react';
import Switch from 'react-switch';
import AsyncSelect from 'react-select/async';
import * as s from './styled-machine-form';
import { removeInvalidChars } from '../../utils';
import { themes } from '../../assets';
import { Loader, VariableLinker } from '..';
import {
  getMaquinaAppliances,
  getVariables,
  getSetPoints,
  getBlueboxSuggestedVariables,
} from '../../services';

const listVoltagem = [
  { label: '110v', value: 110 },
  { label: '220v', value: 220 },
];

// SVG inline do check (Lucide check) — usado dentro do .box do
// checkbox custom. Fica oculto via opacity ate o .box receber estado
// $on=true via styled-component.
const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// SVG inline do X (Lucide x) — botao de fechar no header.
const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const normalizeTypeLabel = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const getOptionTypeLabel = (value, options = []) => {
  if (typeof value === 'object') {
    return normalizeTypeLabel(value?.type || value?.label);
  }

  const option = options.find((item) => String(item.value) === String(value));
  return normalizeTypeLabel(option?.type || option?.label || value);
};

const isApplicationTypeOption = (option) =>
  ['ucha', 'eba'].includes(getOptionTypeLabel(option));

const isEbaTypeOption = (option) => getOptionTypeLabel(option) === 'eba';

const getApplianceTypeLabel = (appliance) => {
  const type =
    appliance?.type?.type ||
    appliance?.applianceType?.type ||
    appliance?.appliance_type?.type ||
    appliance?.type;

  if (typeof type === 'object') {
    return normalizeTypeLabel(type.type || type.label);
  }

  if (typeof type === 'string') {
    return normalizeTypeLabel(type);
  }

  return '';
};

const canUseApplianceForBluebox = (appliance) => {
  const type = getApplianceTypeLabel(appliance);
  return !type || type === 'eba';
};

export default ({
  id = 'maquina',
  onClose,
  onCloseConfirmation,
  submit,
  title,
  dadosAbaixo,
  sucesso,
  iconFinal,
  estacaoConcluido,
  setConfirmationModal,
  setEstacaoConcluido,
  labelButton,
  formikInitialValues,
  listSelectsLists,
  isEdicao,
  loading = false,
}) => {
  const asyncSelectStyles = s.getAsyncSelectStyles();
  const {
    listEstacao,
    listType,
    listThingType,
    blueboxSuggestedVariables = [],
  } = listSelectsLists || {};
  const [maquina, setMaquina] = useState({});
  const [useExistingAppliance, setUseExistingAppliance] = useState(false);
  const [search, setSearch] = useState(null);
  const [commandsSetPoints, setCommandsSetPoints] = useState([]);
  const [commandsVariables, setCommandsVariables] = useState([]);
  const [checkboxSetPointsState, setCheckboxSetPointsState] = useState({});
  const [checkboxVariablesState, setCheckboxVariablesState] = useState({});
  const [showBlueboxSuggestionModal, setShowBlueboxSuggestionModal] =
    useState(false);
  const [blueboxSuggestionPrompted, setBlueboxSuggestionPrompted] =
    useState(false);
  const [blueboxSelectionState, setBlueboxSelectionState] = useState({});
  const [loadedBlueboxSuggestedVariables, setLoadedBlueboxSuggestedVariables] =
    useState(null);

  // Tab ativa. 'dados' = form atual; 'variaveis' = VariableLinker
  // (dual-pane). Tabs sao exibidos apenas em isEdicao porque criar uma
  // maquina ainda nao tem thingId pra vincular variaveis.
  const [activeTab, setActiveTab] = useState('dados');

  const handleCheckboxChangeSetPoints = (event) => {
    const { id: checkboxId, checked } = event.target;
    setCheckboxSetPointsState({
      ...checkboxSetPointsState,
      [checkboxId]: checked,
    });
  };

  const handleCheckboxChangeVariables = (event) => {
    const { id: checkboxId, checked } = event.target;
    setCheckboxVariablesState({
      ...checkboxVariablesState,
      [checkboxId]: checked,
    });
  };

  const setPointsParsed = commandsSetPoints.map((command) => ({
    id: command.value,
    label: command.label,
    enabled: checkboxSetPointsState[command.value] || false,
  }));

  const variablesParsed = commandsVariables.map((command) => ({
    id: command.value,
    label: command.label,
    enabled: checkboxVariablesState[command.value] || false,
  }));

  const blueboxSuggestions =
    loadedBlueboxSuggestedVariables || blueboxSuggestedVariables;

  const validBlueboxSuggestions = blueboxSuggestions.filter(
    (variable) => variable?.id && variable.found !== false
  );

  const selectedBlueboxCount = validBlueboxSuggestions.filter(
    (variable) => blueboxSelectionState[variable.id] !== false
  ).length;

  const allBlueboxSuggestionsSelected =
    validBlueboxSuggestions.length > 0 &&
    selectedBlueboxCount === validBlueboxSuggestions.length;

  const getBlueboxVariableLabel = (variable) => {
    const name = variable.ihmname || variable.name || variable.variable;
    const description = variable.description || variable.label;
    const unit = variable.unit ? ` (${variable.unit})` : '';

    if (name && description && name !== description) {
      return `${name} - ${description}${unit}`;
    }

    return `${name || description || `Variável ${variable.id}`}${unit}`;
  };

  const buildBlueboxSelectionState = (variables = validBlueboxSuggestions) =>
    variables.reduce(
      (acc, variable) => ({
        ...acc,
        [variable.id]: variable.selected !== false,
      }),
      {}
    );

  const filteredListType = (listType || []).filter(isApplicationTypeOption);
  const ebaTypeOption = filteredListType.find(isEbaTypeOption);

  const isBlueboxType = (value) => {
    const rawValue = typeof value === 'object' ? value?.value : value;
    const label = getOptionTypeLabel(value, listThingType || []);

    return label === 'bluebox' || (!label && Number(rawValue) === 3);
  };

  const selectedMachineIsBluebox = isBlueboxType(maquina?.tipoMaquina);

  useEffect(() => {
    if (!selectedMachineIsBluebox || !ebaTypeOption) {
      return;
    }

    setMaquina((oldValues) => {
      const rawType =
        typeof oldValues?.tipo === 'object'
          ? oldValues?.tipo?.value
          : oldValues?.tipo;

      if (String(rawType) === String(ebaTypeOption.value)) {
        return oldValues;
      }

      return {
        ...oldValues,
        tipo: ebaTypeOption,
      };
    });
  }, [ebaTypeOption, selectedMachineIsBluebox]);

  const openBlueboxSuggestionModal = async () => {
    let suggestions = validBlueboxSuggestions;

    if (suggestions.length === 0) {
      try {
        const { data } = await getBlueboxSuggestedVariables();
        setLoadedBlueboxSuggestedVariables(data || []);
        suggestions = (data || []).filter(
          (variable) => variable?.id && variable.found !== false
        );
      } catch (error) {
        suggestions = [];
      }
    }

    if (suggestions.length === 0) {
      return false;
    }

    setBlueboxSelectionState(buildBlueboxSelectionState(suggestions));
    setShowBlueboxSuggestionModal(true);
    return true;
  };

  const handleBlueboxSuggestionToggle = (variableId) => {
    setBlueboxSelectionState((oldValues) => ({
      ...oldValues,
      [variableId]: oldValues[variableId] === false,
    }));
  };

  const handleToggleAllBlueboxSuggestions = () => {
    const nextValue = !allBlueboxSuggestionsSelected;

    setBlueboxSelectionState(
      validBlueboxSuggestions.reduce(
        (acc, variable) => ({
          ...acc,
          [variable.id]: nextValue,
        }),
        {}
      )
    );
  };

  const getEnabledSetpoints = () =>
    setPointsParsed.map((setPoint) => ({
      id: setPoint.id,
      enabled: setPoint.enabled,
    }));

  const getEnabledVariables = (extraEnabledVariableIds = []) => {
    const variablesById = new Map(
      variablesParsed
        .filter((variables) => variables.enabled)
        .map((variables) => [
          Number(variables.id),
          {
            id: variables.id,
            enabled: variables.enabled,
          },
        ])
    );

    extraEnabledVariableIds.forEach((variableId) => {
      variablesById.set(Number(variableId), {
        id: Number(variableId),
        enabled: true,
      });
    });

    return Array.from(variablesById.values());
  };

  const submitMachine = (extraEnabledVariableIds = []) => {
    if (setConfirmationModal) setConfirmationModal(true);
    submit({
      ...maquina,
      estacao: maquina?.estacao?.value || maquina?.estacao || '',
      tipo: maquina?.tipo?.value || maquina?.tipo || '',
      tipoMaquina: maquina?.tipoMaquina?.value || maquina?.tipoMaquina || '',
      voltagem: maquina?.voltagem?.value || 220,
      enabledSetpoints: getEnabledSetpoints(),
      enabledVariables: getEnabledVariables(extraEnabledVariableIds),
    });
  };

  const confirmBlueboxSuggestions = () => {
    const selectedSuggestionIds = validBlueboxSuggestions
      .filter((variable) => blueboxSelectionState[variable.id] !== false)
      .map((variable) => variable.id);

    setCommandsVariables((oldValues) => {
      const variablesById = new Map(oldValues.map((cmd) => [cmd.value, cmd]));

      validBlueboxSuggestions.forEach((variable) => {
        if (!variablesById.has(variable.id)) {
          variablesById.set(variable.id, {
            value: Number(variable.id),
            label: getBlueboxVariableLabel(variable),
            enabled: false,
          });
        }
      });

      return Array.from(variablesById.values());
    });

    setCheckboxVariablesState((oldValues) => {
      const nextValues = { ...oldValues };

      validBlueboxSuggestions.forEach((variable) => {
        nextValues[variable.id] = blueboxSelectionState[variable.id] !== false;
      });

      return nextValues;
    });

    setBlueboxSuggestionPrompted(true);
    setShowBlueboxSuggestionModal(false);
    submitMachine(selectedSuggestionIds);
  };

  const ignoreBlueboxSuggestions = () => {
    setBlueboxSuggestionPrompted(true);
    setShowBlueboxSuggestionModal(false);
    submitMachine();
  };

  const onSubmit = async () => {
    if (!isEdicao && selectedMachineIsBluebox && !blueboxSuggestionPrompted) {
      const opened = await openBlueboxSuggestionModal();
      if (opened) return;
    }

    submitMachine();
  };

  const handleSearchAppliance = useCallback(
    (text, callback) =>
      getMaquinaAppliances(text).then(({ data }) => {
        const appliances = selectedMachineIsBluebox
          ? data.filter(canUseApplianceForBluebox)
          : data;

        callback(
          appliances.map((appliance) => ({
            label: appliance.description,
            value: appliance.id,
            type: getApplianceTypeLabel(appliance),
          }))
        );
      }),
    [selectedMachineIsBluebox]
  );

  const handleChangeMaquinaField = (field, value) => {
    setMaquina((oldValues) => ({
      ...oldValues,
      [field]: typeof value === 'string' ? value.toUpperCase() : value,
    }));
  };

  const checkIfHaveFieldBlank = () =>
    isEdicao
      ? !maquina.appliance ||
        !maquina.modelo ||
        !maquina.tipo ||
        !maquina.estacao
      : (!maquina.applianceId && !maquina.appliance) ||
        !maquina.modelo ||
        !maquina.name ||
        !maquina.tipo ||
        !maquina.tipoMaquina ||
        !maquina.estacao;

  useEffect(() => {
    const initialMachineValues = { ...formikInitialValues };

    if (listEstacao) {
      listEstacao.forEach((estacao) => {
        if (estacao.value === initialMachineValues.estacao)
          initialMachineValues.estacao = estacao;
      });
    }
    if (listType) {
      listType.forEach((type) => {
        if (type.value === initialMachineValues.tipo)
          initialMachineValues.tipo = type;
      });
    }
    if (listThingType) {
      listThingType.forEach((thingType) => {
        if (thingType.value === initialMachineValues.tipoMaquina)
          initialMachineValues.tipoMaquina = thingType;
      });
    }
    if (listVoltagem) {
      listVoltagem.forEach((voltagem) => {
        if (voltagem.value === initialMachineValues.voltagem)
          initialMachineValues.voltagem = voltagem;
      });
    }

    if (initialMachineValues.enabledSetpointThing) {
      const commandSetPointsParsed =
        initialMachineValues.enabledSetpointThing.map((cmd) => {
          return {
            value: Number(cmd.setpoint.id),
            label:
              cmd.setpoint?.name ||
              cmd.setpoint?.label ||
              cmd.setpoint?.variable,
            enabled: cmd.enabled,
          };
        });

      let setPointsChecked = {};
      commandSetPointsParsed.forEach((cmd) => {
        setPointsChecked = {
          ...setPointsChecked,
          [cmd.value]: cmd.enabled,
        };
      });

      setCommandsSetPoints(commandSetPointsParsed);
      setCheckboxSetPointsState(setPointsChecked);
    }

    if (initialMachineValues.enabledVariableThing) {
      const commandVariablesParsed =
        initialMachineValues.enabledVariableThing.map((cmd) => {
          return {
            value: Number(cmd.variable.id),
            label:
              cmd.variable?.name ||
              cmd.variable?.label ||
              cmd.variable?.variable,
            enabled: cmd.enabled,
          };
        });

      setCommandsVariables(commandVariablesParsed);

      let variablesChecked = {};
      commandVariablesParsed.forEach((cmd) => {
        variablesChecked = {
          ...variablesChecked,
          [cmd.value]: cmd.enabled,
        };
      });

      setCheckboxVariablesState(variablesChecked);
    }

    setMaquina(initialMachineValues);
  }, [formikInitialValues, listEstacao, listType, listThingType]);

  useEffect(() => {
    async function getData() {
      const [variables, setpoints] = await Promise.all([
        getVariables(),
        getSetPoints(),
      ]);

      const variablesDefaultParsed = variables.data.map((cmd) => ({
        value: Number(cmd.id),
        label:
          cmd?.ihmname && cmd?.description
            ? `${cmd.ihmname} - ${cmd.description}${
                cmd.unit ? ` (${cmd.unit})` : ''
              }`
            : cmd?.name || cmd?.label || cmd?.variable,
        enabled: false,
      }));

      setCommandsVariables(variablesDefaultParsed);

      const setpointsDefaultParsed = setpoints.data.map((cmd) => ({
        value: Number(cmd.id),
        label: cmd?.name || cmd?.label || cmd?.variable,
        enabled: false,
      }));

      setCommandsSetPoints(setpointsDefaultParsed);
    }

    if (formikInitialValues?.enabledVariableThing === undefined) {
      getData();
    }
    // Mount-only: busca dados base de comandos/variaveis ao abrir o modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click no scrim fecha o modal — mas so se o target for o proprio
  // scrim (nao um filho). Evita fechar ao clicar dentro do modal.
  const handleScrimClick = (e) => {
    if (e.target.id === id && onCloseConfirmation) {
      onCloseConfirmation(true);
    }
  };

  const handleClose = () => {
    if (onCloseConfirmation) onCloseConfirmation(true);
    else if (onClose) onClose();
  };

  const isPumpMachine =
    maquina?.tipoMaquina?.label === 'bomba' ||
    maquina?.tipoMaquina === 'bomba';
  // Mask deixada como referencia visual — usaremos placeholder somente
  // ja que substituimos InputMask por <input> nativo.
  const modeloPlaceholder = isPumpMachine ? 'EX: AB-123/45' : 'EX: TGA-1234/567';

  // Helper pra obter o value bruto de um campo que pode ser tanto
  // option { label, value } quanto valor primitivo.
  const getVal = (field) => {
    const v = maquina?.[field];
    if (v === undefined || v === null) return '';
    if (typeof v === 'object') return v.value ?? '';
    return v;
  };

  // Quando o usuario muda um select nativo, precisamos achar a option
  // original na list (pra preservar o formato { label, value } que o
  // resto do codigo espera) — se nao acharmos, salvamos o valor cru.
  const handleSelectChange = (field, list, rawValue) => {
    const option = list?.find((o) => String(o.value) === String(rawValue));
    handleChangeMaquinaField(field, option || rawValue);

    if (field !== 'tipoMaquina') {
      return;
    }

    if (isBlueboxType(option || rawValue)) {
      if (ebaTypeOption) {
        handleChangeMaquinaField('tipo', ebaTypeOption);
      }
      return;
    }

    setBlueboxSuggestionPrompted(false);
    setShowBlueboxSuggestionModal(false);
  };

  // Quando estamos no modo edicao + tab variaveis, o footer com
  // botao Salvar fica escondido (VariableLinker tem botao proprio).
  const showFooter = !(isEdicao && activeTab === 'variaveis');

  return (
    <s.Scrim id={id} onClick={handleScrimClick}>
      <s.Modal
        role="dialog"
        aria-modal="true"
        aria-label={title}
        $variableTab={isEdicao && activeTab === 'variaveis'}
        onClick={(e) => e.stopPropagation()}
      >
        {showBlueboxSuggestionModal && (
          <s.SuggestionScrim onClick={() => setShowBlueboxSuggestionModal(false)}>
            <s.SuggestionModal
              role="dialog"
              aria-modal="true"
              aria-label="Variáveis padrão Bluebox"
              onClick={(e) => e.stopPropagation()}
            >
              <s.SuggestionHead>
                <s.HeadText>
                  <s.Title>Variáveis padrão Bluebox</s.Title>
                  <s.Desc>
                    Selecione as variáveis que devem ser vinculadas a esta
                    máquina.
                  </s.Desc>
                </s.HeadText>
                <s.Close
                  type="button"
                  aria-label="Fechar sugestões Bluebox"
                  title="Fechar"
                  onClick={() => setShowBlueboxSuggestionModal(false)}
                >
                  <CloseIcon />
                </s.Close>
              </s.SuggestionHead>

              <s.SuggestionToolbar>
                <s.Check
                  $on={allBlueboxSuggestionsSelected}
                  title="Selecionar todas"
                >
                  <input
                    className="sr-only"
                    type="checkbox"
                    checked={allBlueboxSuggestionsSelected}
                    onChange={handleToggleAllBlueboxSuggestions}
                  />
                  <span className="box">
                    <CheckIcon />
                  </span>
                  Selecionar todas
                </s.Check>
                <span>
                  {selectedBlueboxCount} de {validBlueboxSuggestions.length}{' '}
                  selecionadas
                </span>
              </s.SuggestionToolbar>

              <s.SuggestionList>
                {validBlueboxSuggestions.map((variable) => {
                  const checked = blueboxSelectionState[variable.id] !== false;

                  return (
                    <s.SuggestionItem key={variable.id} $on={checked}>
                      <s.Check $on={checked}>
                        <input
                          className="sr-only"
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            handleBlueboxSuggestionToggle(variable.id)
                          }
                        />
                        <span className="box">
                          <CheckIcon />
                        </span>
                        <span className="suggestion-name">
                          {variable.ihmname || variable.name || variable.id}
                        </span>
                      </s.Check>
                      <span className="suggestion-description">
                        {variable.description || variable.label || '-'}
                      </span>
                      <span className="suggestion-unit">
                        {variable.unit || '-'}
                      </span>
                    </s.SuggestionItem>
                  );
                })}
              </s.SuggestionList>

              <s.SuggestionFoot>
                <s.CancelBtn
                  type="button"
                  onClick={ignoreBlueboxSuggestions}
                >
                  Ignorar
                </s.CancelBtn>
                <s.SaveBtn
                  type="button"
                  disabled={selectedBlueboxCount === 0}
                  onClick={confirmBlueboxSuggestions}
                >
                  Usar variáveis
                </s.SaveBtn>
              </s.SuggestionFoot>
            </s.SuggestionModal>
          </s.SuggestionScrim>
        )}

        <s.ModalHead>
          <s.HeadText>
            <s.Title>{title}</s.Title>
            {!estacaoConcluido && dadosAbaixo && <s.Desc>{dadosAbaixo}</s.Desc>}
            {!estacaoConcluido && (
              <s.ReqHint>
                <span className="req">*</span>Itens obrigatórios
              </s.ReqHint>
            )}
          </s.HeadText>
          <s.Close
            type="button"
            aria-label="Fechar"
            onClick={handleClose}
            title="Fechar"
          >
            <CloseIcon />
          </s.Close>
        </s.ModalHead>

        {!estacaoConcluido && isEdicao && (
          <s.TabsRow>
            <s.TabButton
              type="button"
              $active={activeTab === 'dados'}
              onClick={() => setActiveTab('dados')}
            >
              Dados da máquina
            </s.TabButton>
            <s.TabButton
              type="button"
              $active={activeTab === 'variaveis'}
              onClick={() => setActiveTab('variaveis')}
            >
              Vincular variáveis
            </s.TabButton>
          </s.TabsRow>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <s.ModalBody
            $variableTab={isEdicao && activeTab === 'variaveis'}
          >
            {estacaoConcluido ? (
              <s.SuccessBox>
                {iconFinal && <s.SuccessIcon src={iconFinal} alt="" />}
                <s.SuccessText>{sucesso}</s.SuccessText>
              </s.SuccessBox>
            ) : isEdicao && activeTab === 'variaveis' ? (
              <VariableLinker thingId={formikInitialValues?.id} />
            ) : (
              <>
                {!isEdicao && (
                  <s.ToggleRow>
                    <Switch
                      onColor={themes.statusN}
                      offColor={themes.darkerblue}
                      onHandleColor={themes.white}
                      offHandleColor={themes.white}
                      checkedIcon={false}
                      uncheckedIcon={false}
                      checked={useExistingAppliance}
                      onChange={(newValue) => {
                        handleChangeMaquinaField('appliance', '');
                        handleChangeMaquinaField('applianceId', '');
                        setUseExistingAppliance(newValue);
                      }}
                      height={20}
                      width={42}
                    />
                    <span className="toggle-label">
                      Usar aplicação já existente
                    </span>
                  </s.ToggleRow>
                )}

                <s.FormGrid>
                  {!isEdicao && (
                    <s.Field>
                      <s.Label htmlFor="name">
                        Nome da Máquina <span className="req">*</span>
                      </s.Label>
                      <s.Input
                        id="name"
                        name="name"
                        type="text"
                        maxLength={30}
                        placeholder="Ex.: SABESPVilaSonia"
                        value={maquina.name || ''}
                        onChange={(e) =>
                          handleChangeMaquinaField(
                            'name',
                            removeInvalidChars(e).target.value
                          )
                        }
                      />
                    </s.Field>
                  )}

                  <s.Field>
                    <s.Label htmlFor="estacao">
                      Nome da Estação <span className="req">*</span>
                    </s.Label>
                    <s.Select
                      id="estacao"
                      value={getVal('estacao')}
                      onChange={(e) =>
                        handleSelectChange(
                          'estacao',
                          listEstacao,
                          e.target.value
                        )
                      }
                    >
                      <option value="">Selecione</option>
                      {(listEstacao || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </s.Select>
                  </s.Field>

                  <s.Field>
                    <s.Label htmlFor="tipoMaquina">
                      Tipo de Máquina <span className="req">*</span>
                    </s.Label>
                    <s.Select
                      id="tipoMaquina"
                      value={getVal('tipoMaquina')}
                      disabled={isEdicao}
                      onChange={(e) =>
                        handleSelectChange(
                          'tipoMaquina',
                          listThingType,
                          e.target.value
                        )
                      }
                    >
                      <option value="">Selecione</option>
                      {(listThingType || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </s.Select>
                  </s.Field>

                  <s.Field>
                    <s.Label htmlFor="tipo">
                      Tipo <span className="req">*</span>
                    </s.Label>
                    <s.Select
                      id="tipo"
                      value={getVal('tipo')}
                      disabled={selectedMachineIsBluebox}
                      onChange={(e) =>
                        handleSelectChange(
                          'tipo',
                          filteredListType,
                          e.target.value
                        )
                      }
                    >
                      <option value="">Selecione</option>
                      {(selectedMachineIsBluebox && ebaTypeOption
                        ? [ebaTypeOption]
                        : filteredListType
                      ).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </s.Select>
                  </s.Field>

                  <s.Field>
                    <s.Label htmlFor="modelo">
                      Modelo <span className="req">*</span>
                    </s.Label>
                    <s.Input
                      id="modelo"
                      name="modelo"
                      type="text"
                      maxLength={20}
                      placeholder={modeloPlaceholder}
                      value={maquina.modelo || ''}
                      onChange={(e) =>
                        handleChangeMaquinaField(
                          'modelo',
                          removeInvalidChars(e).target.value
                        )
                      }
                    />
                  </s.Field>

                  <s.Field>
                    <s.Label htmlFor="serialNumber">Número de série</s.Label>
                    <s.Input
                      id="serialNumber"
                      name="serialNumber"
                      type="text"
                      maxLength={60}
                      placeholder="Opcional"
                      value={maquina.serialNumber || ''}
                      onChange={(e) =>
                        handleChangeMaquinaField(
                          'serialNumber',
                          removeInvalidChars(e).target.value
                        )
                      }
                    />
                  </s.Field>

                  {!useExistingAppliance ? (
                    <s.Field $full={isEdicao}>
                      <s.Label htmlFor="appliance">
                        Descrição da Aplicação <span className="req">*</span>
                      </s.Label>
                      <s.Input
                        id="appliance"
                        name="appliance"
                        type="text"
                        maxLength={30}
                        placeholder="Ex.: UCHA - SABESP Vila Sônia"
                        value={maquina.appliance || ''}
                        onChange={(e) =>
                          handleChangeMaquinaField(
                            'appliance',
                            removeInvalidChars(e).target.value
                          )
                        }
                      />
                    </s.Field>
                  ) : (
                    <s.Field>
                      <s.Label htmlFor="applianceSearch">
                        Pesquisar aplicação <span className="req">*</span>
                      </s.Label>
                      <s.AsyncWrap>
                        <AsyncSelect
                          inputId="applianceSearch"
                          styles={asyncSelectStyles}
                          loadOptions={(text, callback) => {
                            clearTimeout(search);
                            setSearch(
                              setTimeout(() => {
                                handleSearchAppliance(text, callback);
                              }, 500)
                            );
                          }}
                          onChange={(opt) => {
                            handleChangeMaquinaField('appliance', opt.label);
                            handleChangeMaquinaField('applianceId', opt.value);
                          }}
                          noOptionsMessage={() => 'Sem opções'}
                          loadingMessage={() => 'Buscando...'}
                          placeholder="Ex.: UCHA - SABESP Vila Sônia"
                        />
                      </s.AsyncWrap>
                    </s.Field>
                  )}
                </s.FormGrid>

                {setPointsParsed.length > 0 && (
                  <s.Group>
                    <s.GroupTitle>Setpoints</s.GroupTitle>
                    <s.Checks2>
                      {setPointsParsed.map((command) => (
                        <s.Check key={command.id} $on={command.enabled}>
                          <input
                            className="sr-only"
                            type="checkbox"
                            id={command.id}
                            checked={command.enabled}
                            onChange={handleCheckboxChangeSetPoints}
                          />
                          <span className="box">
                            <CheckIcon />
                          </span>
                          {command.label}
                        </s.Check>
                      ))}
                    </s.Checks2>
                  </s.Group>
                )}

                {variablesParsed.length > 0 && (
                  <s.Group>
                    <s.GroupTitle>Comandos</s.GroupTitle>
                    <s.Checks3>
                      {variablesParsed.map((command) => (
                        <s.Check key={command.id} $on={command.enabled}>
                          <input
                            className="sr-only"
                            type="checkbox"
                            id={command.id}
                            checked={command.enabled}
                            onChange={handleCheckboxChangeVariables}
                          />
                          <span className="box">
                            <CheckIcon />
                          </span>
                          {command.label}
                        </s.Check>
                      ))}
                    </s.Checks3>
                  </s.Group>
                )}
              </>
            )}
          </s.ModalBody>

          {estacaoConcluido ? (
            <s.ModalFoot>
              <s.SaveBtn
                type="button"
                onClick={() => {
                  setMaquina({});
                  onClose();
                  if (setEstacaoConcluido) setEstacaoConcluido(false);
                }}
              >
                Concluir
              </s.SaveBtn>
            </s.ModalFoot>
          ) : (
            showFooter && (
              <s.ModalFoot>
                <s.ConfirmText>
                  {isEdicao
                    ? 'Confirme suas modificações'
                    : 'Confirme o cadastro'}
                </s.ConfirmText>
                <s.CancelBtn type="button" onClick={handleClose}>
                  Cancelar
                </s.CancelBtn>
                <s.SaveBtn
                  type="submit"
                  disabled={loading || checkIfHaveFieldBlank()}
                >
                  {loading ? (
                    <Loader width={22} height={22} />
                  ) : (
                    labelButton || 'Salvar'
                  )}
                </s.SaveBtn>
              </s.ModalFoot>
            )
          )}
        </form>
      </s.Modal>
    </s.Scrim>
  );
};
